import PreviewLayout from "./preview-layout";
import PreviewField from "./preview-field";
import type { FlowSection, FlowSectionField } from "../form-builder/types";
import type { BrandKit } from "@/modules/brand";

interface PreviewPersonalDetailProps {
	id: string;
	section: FlowSection;
	brandName: string;
	brandLogo: string | null;
	brandkit: BrandKit;
	isSelected: boolean;
	onTitleChange: (title: string) => void;
	onDescriptionChange: (description: string) => void;
	onFieldUpdate?: (fieldId: string, updates: Partial<FlowSectionField>) => void;
	isDarkMode?: boolean;
}

export default function PreviewPersonalDetail({ id, section, brandName, brandLogo, brandkit, isSelected, onTitleChange, onDescriptionChange, onFieldUpdate, isDarkMode = false }: PreviewPersonalDetailProps) {
	void isSelected;

	const visibleFields = (section.fields ?? []).filter((f) => f.is_visible).sort((a, b) => a.sort_order - b.sort_order);

	return (
		<PreviewLayout
			id={id}
			brandName={brandName}
			brandLogo={brandLogo}
			brandkit={brandkit}
			title={section.title}
			titlePlaceholder="Tell us about yourself"
			subtitle={section.description}
			subtitlePlaceholder="Add a description..."
			showBackButton
			progress={{ current: 4, total: 6 }}
			centered={false}
			editableTitle
			editableSubtitle
			onLocalTitleChange={onTitleChange}
			onLocalSubtitleChange={onDescriptionChange}
			isDarkMode={isDarkMode}
		>
			<div className="space-y-8">
				{visibleFields.length > 0 ? (
					visibleFields.map((field) => (
						<PreviewField
							key={field.id}
							field={field}
							onLabelChange={(label) => onFieldUpdate?.(field.id, { label })}
							onPlaceholderChange={(placeholder) => onFieldUpdate?.(field.id, { placeholder })}
							isDarkMode={isDarkMode}
						/>
					))
				) : (
					<>
						{/* Fallback: hardcoded fields for backwards compatibility */}
						<div>
							<label className={`block font-medium text-sm mb-2 ${isDarkMode ? "text-white" : "text-primary"}`} style={{ fontFamily: "var(--font-body, inherit)" }}>
								Name<span className="text-red-500"> *</span>
							</label>
							<div
								className={`w-full border rounded-lg shadow-sm py-2 px-4 text-sm cursor-not-allowed ${isDarkMode ? "bg-gray-900 border-gray-800" : "bg-white border-gray-200"}`}
								style={{ fontFamily: "var(--font-body, inherit)" }}
							>
								<span className={`font-light ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>Jane Smith</span>
							</div>
						</div>
						<div>
							<label className={`block font-medium text-sm mb-2 ${isDarkMode ? "text-white" : "text-primary"}`} style={{ fontFamily: "var(--font-body, inherit)" }}>
								Email
							</label>
							<div
								className={`w-full border rounded-lg shadow-sm py-2 px-4 text-sm cursor-not-allowed ${isDarkMode ? "bg-gray-900 border-gray-800" : "bg-white border-gray-200"}`}
								style={{ fontFamily: "var(--font-body, inherit)" }}
							>
								<span className={`font-light ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>jane@company.com</span>
							</div>
						</div>
						<div>
							<label className={`block font-medium text-sm mb-2 ${isDarkMode ? "text-white" : "text-primary"}`} style={{ fontFamily: "var(--font-body, inherit)" }}>
								Your photo
							</label>
							<div
								className={`w-32.5 aspect-square border border-dashed rounded-lg flex items-center justify-center cursor-not-allowed ${isDarkMode ? "border-gray-700 bg-gray-900" : "border-gray-300 bg-gray-50"}`}
							>
								<svg className={`w-8 h-8 ${isDarkMode ? "text-gray-600" : "text-gray-300"}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z"
									/>
								</svg>
							</div>
						</div>
					</>
				)}
			</div>
		</PreviewLayout>
	);
}
